// frontend/plugins/extra-packs-loader.client.ts
/**
 * Extra Packs Route-Based Loader Plugin
 * 
 * This plugin ensures that the extra packs data is only loaded when on relevant routes:
 * - /extra-packs
 * - /account/dashboard?view=extra-roaming
 * 
 * By using a plugin, we can hook into Nuxt's routing system and only
 * initialize the store when needed, preventing unnecessary GraphQL queries.
 */

import { defineNuxtPlugin, useRoute } from '#app'
import { useExtraPacksStore } from '~/composables/useExtraPacksStore'

export default defineNuxtPlugin((nuxtApp) => {
  // Execute this only on client-side
  if (process.client) {
    // Initial check on first load
    const route = useRoute()
    const extraPacksStore = useExtraPacksStore()
    
    // Check if we're on a relevant route
    const isRelevantRoute = 
      route.path.startsWith('/extra-packs') || 
      (route.path.startsWith('/account/dashboard') && route.query.view === 'extra-roaming')
    
    if (isRelevantRoute) {
      console.log('[ExtraPacksPlugin] On relevant route, initializing store')
      extraPacksStore.initialize().catch(err => {
        console.error('[ExtraPacksPlugin] Error initializing store:', err)
      })
    }
    
    // Add route change hook to handle navigation
    nuxtApp.hook('page:finish', () => {
      const currentRoute = useRoute()
      
      // Check if we're on a relevant route after navigation
      const isRelevantRouteAfterNav = 
        currentRoute.path.startsWith('/extra-packs') || 
        (currentRoute.path.startsWith('/account/dashboard') && currentRoute.query.view === 'extra-roaming')
      
      if (isRelevantRouteAfterNav) {
        console.log('[ExtraPacksPlugin] Navigated to relevant route, initializing store')
        extraPacksStore.initialize().catch(err => {
          console.error('[ExtraPacksPlugin] Error initializing store after navigation:', err)
        })
      }
    })
    
    // Return plugin API
    return {
      provide: {
        extraPacksLoader: {
          forceLoad: () => {
            console.log('[ExtraPacksPlugin] Manually forcing load of extra packs data')
            return extraPacksStore.initialize(true)
          }
        }
      }
    }
  }
})
